// Problem taxonomy for agent proposals, plus the formatter that turns a batch
// of problems into the short, structured rejection an LLM can actually act on.
//
// Why this exists: the validator used to hand the agent a flat list of up to 30
// near-identical strings ("…id X does not match any location…") followed by
// "…and 512 more". That hides the only fact that matters — that ALMOST EVERY id
// was wrong, which means the ids were typed from memory rather than resolved
// from data. Grouping by `kind` surfaces the pattern in the first line, and the
// per-kind hint names the fix (pass a dataHandle, resolve against the
// `locations` global) instead of leaving the model to re-guess 542 ids.

// Stable kinds. They are part of the tool contract: the payload sent back to the
// agent carries `problemCounts` keyed by these, so renaming one is a breaking
// change to what the model sees.
export const PROBLEM_KINDS = Object.freeze({
  EMPTY_PROPOSAL: 'emptyProposal',
  NOT_A_TASK: 'notATaskObject',
  DUPLICATE_TASK_ID: 'duplicateTaskId',
  MISSING_TITLE: 'missingTitle',
  INVALID_ARRAY_FIELD: 'invalidArrayField',
  INVALID_SUBTASK: 'invalidSubtask',
  MISSING_SUBTASK_TITLE: 'missingSubtaskTitle',
  UNKNOWN_ITEM_TEMPLATE_CODE: 'unknownItemTemplateCode',
  INVALID_ITEM_TYPE: 'invalidItemType',
  ITEM_TYPE_TEMPLATE_CONFLICT: 'itemTypeTemplateConflict',
  INVALID_WEIGHTING: 'invalidWeighting',
  UNKNOWN_TEAM_ID: 'unknownTeamId',
  UNKNOWN_LOCATION_ID: 'unknownLocationId',
  INVALID_TIME_RANGES: 'invalidTimeRanges',
  INVALID_TIME_RANGE_ROW: 'invalidTimeRangeRow',
  UNKNOWN_TIME_RANGE_TEMPLATE_ID: 'unknownTimeRangeTemplateId',
  DUPLICATE_TIME_RANGE_ROW: 'duplicateTimeRangeRow',
  MISSING_TIME_RANGE: 'missingTimeRange',
  INVALID_DATE: 'invalidDate',
  MISSING_START_DATE: 'missingStartDate',
  END_BEFORE_START: 'endBeforeStart',
  INVALID_DURATION: 'invalidDuration',
  INVALID_PROGRESS: 'invalidProgress',
  LOCKED_TIME_RANGE: 'lockedTimeRange',
  LOCKED_STRUCTURE: 'lockedStructure',
  LOCKED_SUBTASKS: 'lockedSubtasks',
  LOCKED_PREREQUISITES: 'lockedPrerequisites',
  LOCKED_TASK_DELETED: 'lockedTaskDeleted',
  DUPLICATE_SIBLING_CODE: 'duplicateSiblingCode',
  SELF_PREREQUISITE: 'selfPrerequisite',
  UNKNOWN_PREREQUISITE: 'unknownPrerequisite',
  CIRCULAR_PREREQUISITE: 'circularPrerequisite',
});

export const DEFAULT_MAX_SAMPLES = 10;
// ~2 KB. The whole point is that a rejection costs the model a paragraph, not a
// page: the counts carry the shape of the failure, the samples only illustrate it.
export const DEFAULT_MAX_CHARS = 2048;

const K = PROBLEM_KINDS;

const LOCK_HINT = 'These tasks are locked. Read each task\'s `lock` before proposing and re-propose the '
  + 'locked fields exactly as they are — inspect the loaded schedule first if you are unsure.';

// One hint per kind, phrased as the next action. `stats` gives the hint a way to
// branch on how bad the failure is (see unknownLocationId).
const HINTS = {
  [K.UNKNOWN_LOCATION_ID]: ({ majority }) => (majority
    ? 'Most of the location ids in this proposal are unknown, which usually means they were typed '
      + 'rather than resolved from data. Build the mapping in a sandbox and pass it as dataHandle, or '
      + 'resolve titles against the `locations` global inside your transform — never retype ids you '
      + 'have only seen in a preview.'
    : 'Resolve these ids against the `locations` global available inside the transform/inspect '
      + 'sandbox, or look them up with rdrive_search_locations.'),
  [K.UNKNOWN_TEAM_ID]: () =>
    'Resolve team ids against the `teams` global inside the sandbox, or use rdrive_search_teams.',
  [K.UNKNOWN_ITEM_TEMPLATE_CODE]: () =>
    'Use rdrive_search_item_templates to get valid process/issue template codes.',
  [K.UNKNOWN_TIME_RANGE_TEMPLATE_ID]: () =>
    'Use the ids from the `scheduleTemplate` global\'s timeRangeTemplates — the schedule template '
    + 'declares the full set.',
  [K.MISSING_TIME_RANGE]: () =>
    'Every declared tracking time range needs a row with a startDate; `scheduleTemplate.timeRangeTemplates` '
    + 'lists which ones this schedule declares.',
  [K.INVALID_DATE]: () => 'Dates must be ISO 8601 strings (e.g. "2026-03-01T00:00:00.000Z").',
  [K.MISSING_START_DATE]: () => 'Dates must be ISO 8601 strings (e.g. "2026-03-01T00:00:00.000Z").',
  [K.END_BEFORE_START]: () => 'Check the date arithmetic — every range must end on or after it starts.',
  [K.DUPLICATE_SIBLING_CODE]: () =>
    'Sibling codes must be unique (case-insensitive). Renumber them, or omit "code" entirely and let '
    + 'the dashboard derive it from tree position.',
  [K.DUPLICATE_TASK_ID]: () => 'Every task in the tree needs a unique id — references are resolved by id.',
  [K.UNKNOWN_PREREQUISITE]: () =>
    'Prerequisite ids must reference tasks that still exist in the proposed tree.',
  [K.SELF_PREREQUISITE]: () =>
    'Prerequisite ids must reference tasks that still exist in the proposed tree.',
  [K.CIRCULAR_PREREQUISITE]: () => 'Break the dependency loop — the graph must be acyclic.',
  [K.LOCKED_TIME_RANGE]: () => LOCK_HINT,
  [K.LOCKED_STRUCTURE]: () => LOCK_HINT,
  [K.LOCKED_SUBTASKS]: () => LOCK_HINT,
  [K.LOCKED_PREREQUISITES]: () => LOCK_HINT,
  [K.LOCKED_TASK_DELETED]: () => LOCK_HINT,
  [K.MISSING_TITLE]: () => 'Every task needs a title; a transform must return the FULL tree, not a diff.',
};

/**
 * Group problems by kind and format the rejection the agent receives.
 *
 * @param {Object} input
 * @param {Array}  input.problems      `{ kind, taskId, taskTitle, message }` objects (may be a
 *                                     capped sample of the full set — counts are authoritative).
 * @param {Object} input.problemCounts `{ kind: n }` over ALL problems.
 * @param {number} input.problemTotal  Total problem count (defaults to the sum of the counts).
 * @param {Object} input.refCounts     `{ location, team, itemTemplateCode }` — how many references
 *                                     of each sort were checked, so a hint can say "most of them".
 * @param {Object} [options]
 * @param {string} [options.headline]    Leading sentence (the caller's rejection banner).
 * @param {number} [options.maxSamples]  Sample messages to include (default 10).
 * @param {number} [options.maxChars]    Hard cap on the whole string (default ~2 KB).
 * @returns {{ error: string, problemCounts: Object, samples: Array }}
 */
export function summarizeProblems({
  problems = [],
  problemCounts = null,
  problemTotal = null,
  refCounts = {},
} = {}, {
  headline = 'Proposal rejected by dashboard validation — nothing was shown to the user.',
  maxSamples = DEFAULT_MAX_SAMPLES,
  maxChars = DEFAULT_MAX_CHARS,
} = {}) {
  const counts = problemCounts && Object.keys(problemCounts).length > 0
    ? { ...problemCounts }
    : problems.reduce((acc, problem) => {
      const kind = problem?.kind || 'unknown';
      acc[kind] = (acc[kind] || 0) + 1;
      return acc;
    }, {});
  const total = Number.isFinite(problemTotal) && problemTotal > 0
    ? problemTotal
    : Object.values(counts).reduce((sum, n) => sum + n, 0);

  const samples = problems.slice(0, maxSamples).map((problem) => ({
    kind: problem.kind,
    taskId: problem.taskId ?? null,
    taskTitle: problem.taskTitle ?? null,
    message: problem.message,
  }));

  if (total === 0) {
    return { error: '', problemCounts: counts, samples: [] };
  }

  // Biggest kind first — that ordering IS the diagnosis on a bulk failure.
  const ranked = Object.entries(counts).sort((a, b) => b[1] - a[1]);
  const countsLine = ranked.map(([kind, n]) => `${kind} ×${n}`).join(', ');
  const head = `${headline} ${total} problem${total === 1 ? '' : 's'} (${countsLine}).`;

  const [dominantKind, dominantCount] = ranked[0];
  const hint = hintFor(dominantKind, dominantCount, { total, refCounts });

  // Budget: the headline and the hint are the load-bearing parts, so samples are
  // what gets dropped when the cap bites.
  const tail = hint ? `\nHint: ${hint}` : '';
  let body = '';
  for (const sample of samples) {
    const line = `\n- ${sample.message}`;
    if (head.length + body.length + line.length + tail.length > maxChars) break;
    body += line;
  }
  let error = `${head}${body}${tail}`;
  if (error.length > maxChars) error = `${error.slice(0, maxChars - 1)}…`;
  return { error, problemCounts: counts, samples };
}

function hintFor(kind, count, { total, refCounts }) {
  const build = HINTS[kind];
  if (!build) return '';
  // "Majority" is measured against how many references of that sort were
  // actually checked when we know it (542 of 585 location ids), and against the
  // problem total otherwise.
  const checked = kind === K.UNKNOWN_LOCATION_ID ? refCounts?.location
    : kind === K.UNKNOWN_TEAM_ID ? refCounts?.team
      : kind === K.UNKNOWN_ITEM_TEMPLATE_CODE ? refCounts?.itemTemplateCode
        : 0;
  const denominator = Number.isFinite(checked) && checked > 0 ? checked : total;
  return build({ count, total, checked, majority: denominator > 0 && count / denominator > 0.5 });
}
